import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { SECTORS, RISK_CONFIG, STAGES } from './pipelineData';
import { ROUTES } from 'config/navConfig';
import { MOCK_SPVS } from 'data/mockData';

export default function DealCard({ deal, onMoveStage, onToggleFlag, onAddToast }) {
  const navigate = useNavigate();
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const sector = SECTORS?.find((s) => s?.id === deal?.sector);
  const linkedAssetName = MOCK_SPVS.find(s => s.dealId === deal?.id && s.assetSnapshot)?.assetSnapshot?.name ?? null;
  const risk = RISK_CONFIG?.[deal?.riskRating];
  const currentStageIdx = STAGES?.findIndex((s) => s?.id === deal?.stage);
  const adjacentStages = STAGES?.filter((_, idx) => idx === currentStageIdx - 1 || idx === currentStageIdx + 1);

  const handleView = (e) => {
    e?.stopPropagation();
    navigate(ROUTES.dealRecord.replace(':id', deal?.id));
  };

  const handleMove = (e, stageId) => {
    e?.stopPropagation();
    onMoveStage?.(deal?.id, stageId);
    setShowMoveMenu(false);
    const stageName = STAGES?.find((s) => s?.id === stageId)?.label;
    onAddToast?.({ type: 'success', title: 'Deal moved', message: deal?.name + ' moved to ' + stageName });
  };

  const handleFlag = (e) => {
    e?.stopPropagation();
    onToggleFlag?.(deal?.id);
  };

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-all duration-200 card-interactive cursor-pointer group relative"
      onClick={handleView}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <h4 className="font-semibold text-slate-800 text-sm leading-snug flex-1">{deal?.name}</h4>
        <button
          onClick={handleFlag}
          className={`flex-shrink-0 p-1 rounded transition-colors ${deal?.flagged ? 'text-amber-500' : 'text-slate-300 hover:text-amber-400'}`}
          title={deal?.flagged ? 'Remove flag' : 'Flag deal'}
        >
          <Icon name="Flag" size={14} strokeWidth={2} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {sector && (
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ color: sector?.color, background: sector?.bg }}
          >
            {sector?.label}
          </span>
        )}
        {deal?.fundId && (
          <button
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-opacity hover:opacity-75"
            style={{ background: 'var(--color-primary-subtle)', color: 'var(--color-primary)' }}
            onClick={(e) => { e.stopPropagation(); navigate(ROUTES.fundDetail.replace(':id', deal.fundId)); }}
          >
            <Icon name="Landmark" size={10} color="var(--color-primary)" />
            {deal.fundId === 'fund-1' ? 'Fund I' : 'Fund II'}
          </button>
        )}
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
          <Icon name="MapPin" size={10} />
          {deal?.region}
        </span>
        {linkedAssetName && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold"
            style={{ background: 'rgba(8,145,178,0.1)', color: '#0891b2', borderRadius: '4px' }}
          >
            <Icon name="Building2" size={10} color="#0891b2" />
            {linkedAssetName}
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">IRR</span>
          <span className="text-xs font-bold text-slate-800">{deal?.targetIRR}%</span>
        </div>
        <div className="w-px h-3 bg-slate-200" />
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">Ticket</span>
          <span className="text-xs font-bold text-slate-800">{String.fromCharCode(163)}{deal?.ticketSize}m</span>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <span
          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
          style={{ color: risk?.color, background: risk?.bg, borderColor: risk?.border }}
        >
          {deal?.riskRating} Risk
        </span>
        <span className="text-xs text-slate-400 flex items-center gap-1">
          <Icon name="Clock" size={10} />
          {deal?.daysInStage}d in stage
        </span>
      </div>

      {/* IC workflow CTA — shown for diligence/ic-review stage deals */}
      {(deal?.stage === 'diligence' || deal?.stage === 'ic-review') && (
        <div className="mt-2.5 pt-2.5 border-t border-slate-100">
          <button
            onClick={(e) => { e?.stopPropagation(); navigate(ROUTES.icSubmission); }}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
            style={deal?.stage === 'ic-review'
              ? { background: 'rgba(217,119,6,0.10)', color: '#d97706' }
              : { background: 'rgba(124,58,237,0.08)', color: '#7c3aed' }
            }
          >
            <Icon name="ClipboardCheck" size={12} strokeWidth={2} />
            {deal?.stage === 'diligence' ? 'Submit to IC' : 'View IC Submission'}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2.5">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: 'var(--color-primary, #3b4c4c)' }}
          title={deal?.assignedName}
        >
          {deal?.assignedTo}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={handleView}
            className="p-1.5 rounded text-slate-400 hover:text-[#3b4c4c] hover:bg-[rgba(59,76,76,0.07)] transition-colors"
            title="View deal"
          >
            <Icon name="Eye" size={14} />
          </button>
          <div className="relative">
            <button
              onClick={(e) => { e?.stopPropagation(); setShowMoveMenu((v) => !v); }}
              className="p-1.5 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              title="Move stage"
            >
              <Icon name="ArrowRightLeft" size={14} />
            </button>
            {showMoveMenu && (
              <div
                className="absolute right-0 bottom-full mb-1 bg-white border border-slate-200 rounded-lg shadow-lg z-20 min-w-40 py-1"
                onClick={(e) => e?.stopPropagation()}
              >
                {adjacentStages?.length === 0 && (
                  <p className="px-3 py-2 text-xs text-slate-400">No adjacent stages</p>
                )}
                {adjacentStages?.map((s) => (
                  <button
                    key={s?.id}
                    onClick={(e) => handleMove(e, s?.id)}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s?.color }} />
                    {s?.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
